import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Dictionary } from "lodash";
import { Observable, of } from "rxjs";
import { catchError, retryWhen } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { genericRetryStrategy } from "src/app/utils/genericRetryStrategy";
import { environment } from "src/environments/environment";
import { GetDataDTO } from "../../schema/Common/GetDataDTO";
import { GrupoTO } from "../../schema/User/GrupoTO";
import { PatchUserAccountTO } from "../../schema/User/PatchUserAccountTO";
import { PatchUseInterestsTO } from "../../schema/User/PatchUserInterestsTO";
import { PatchUserPreferencesTO } from "../../schema/User/PatchUserPreferencesTO";
import { PatchUserSkillsDTO } from "../../schema/User/PatchUserSkillsDTO";
import { TreeViewNodeDTO } from "../../schema/User/TreeView/TreeViewNodeDTO";
import { TreeViewParametersGroupDTO } from "../../schema/User/TreeView/TreeViewParametersGroupDTO";
import { UserCompleteRegistrationDTO } from "../../schema/User/UserCompleteRegistrationDTO";
import { UsuarioInsertTO } from "../../schema/User/UsuarioInsertTO";
import { UsuarioTO } from "../../schema/User/UsuarioTO";

@Injectable({
    providedIn: "root"
})
export class ApiUserService {
    constructor(private http: HttpClient, private authService: AuthService) {}

    create(
        nome: string,
        sobrenome: string,
        email: string,
        tagsInteressesPrincipais: string[],
        tagsInteressesDetalhes: string[],
        senha: string,
        googleToken: string = null,
        facebookToken: string = null,
        isCreationByInvite = false,
        customFields: Dictionary<string> | null = null
    ): Observable<UsuarioTO> {
        const usuarioTO: UsuarioInsertTO = {
            nome: nome,
            sobrenome: sobrenome,
            email: email.toLowerCase(),
            userTempPwd: senha,
            tagsInteressesPrincipais: tagsInteressesPrincipais,
            tagsInteressesDetalhes: tagsInteressesDetalhes,
            subscriptionId: this.authService.getCurrentSubscriptionId(),
            googleToken: googleToken,
            facebookToken: facebookToken,
            isCreationByInvite: isCreationByInvite,
            customFields: customFields
        };

        return this.http.post<UsuarioTO>(`${environment.user.url}/api/v1/users`, JSON.stringify(usuarioTO), {
            headers: {
                "Content-Type": "application/json; charset=utf-8"
            }
        });
    }

    likeQuestion(courseId: string, questionId: string): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/Users/${userId}/LikeQuestion`, {
            courseId,
            questionId
        });
    }

    bookmarkQuestion(courseId: string, questionId: string): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/Users/${userId}/BookmarkQuestion`, {
            courseId,
            questionId
        });
    }

    getUserById(userId: string): Observable<UsuarioTO> {
        return this.getUser(userId).pipe(
            retryWhen(
                genericRetryStrategy({
                    scalingDuration: 2000,
                    excludedStatusCodes: [500, 401],
                    maxRetryAttempts: 10
                })
            ),
            catchError((error) => of(error))
        );
    }

    private getUser(urseId: string): Observable<UsuarioTO> {
        return this.http.get<UsuarioTO>(`${environment.user.url}/api/v1/Users/${urseId}`);
    }

    getUserByEmail(email: string): Observable<UsuarioTO> {
        return this.http.get<UsuarioTO>(`${environment.user.url}/api/v1/Users/exists/${email}`);
    }

    atualizarMatchScore(): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.post<any>(`${environment.user.url}/api/v1/Users/${userId}/UpdateMatchScore`, null);
    }

    public updateUserAccount(data: PatchUserAccountTO): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/users/${userId}/account`, data);
    }

    public updateUserPreferences(data: PatchUserPreferencesTO): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/users/${userId}/preferences`, data);
    }

    public updateUserInterests(data: PatchUseInterestsTO): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/users/${userId}/interests`, data);
    }

    public updateUserSkills(data: PatchUserSkillsDTO): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        return this.http.patch<any>(`${environment.user.url}/api/v1/users/${userId}/skills`, data);
    }

    public updateProfileBanner(file: File): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        const formData = new FormData();
        formData.append("file", file);
        return this.http.patch<any>(`${environment.user.url}/api/v1/Users/${userId}/banner`, formData);
    }

    public updateProfilePic(file: File): Observable<any> {
        const userId = this.authService.getClaimsUserId();
        const formData = new FormData();
        formData.append("file", file);
        return this.http.patch<any>(`${environment.user.url}/api/v1/Users/${userId}/photo`, formData);
    }

    getGroups(
        find = "",
        orderBy = "nome",
        sortOrder = "asc",
        ids = [],
        skip = 0,
        take = 10
    ): Observable<GetDataDTO<GrupoTO>> {
        let params: HttpParams = new HttpParams()
            .set("skip", skip.toString())
            .set("take", take.toString())
            .set("sortOrder", sortOrder.toString())
            .set("orderBy", orderBy);

        if (find) params = params.set("find", find);

        if (ids) {
            ids.forEach((id) => {
                params = params.append("Ids", id);
            });
        }

        return this.http.get<GetDataDTO<GrupoTO>>(`${environment.user.url}/api/v1/Group`, {
            params: params
        });
    }

    get(find = "", orderBy = "nome", sortOrder = "asc", skip = 0, take = 10): Observable<GetDataDTO<UsuarioTO>> {
        const params: HttpParams = new HttpParams()
            .set("skip", skip.toString())
            .set("take", take.toString())
            .set("sortOrder", sortOrder.toString())
            .set("find", find)
            .set("orderBy", orderBy);

        return this.http.get<GetDataDTO<UsuarioTO>>(`${environment.user.url}/api/v1/users`, {
            params: params
        });
    }

    getTreeView(treeViewParametersGroupDTO: TreeViewParametersGroupDTO): Observable<TreeViewNodeDTO<GrupoTO>[]> {
        let params: HttpParams = new HttpParams()
            .set("UseLazyLoading", treeViewParametersGroupDTO.useLazyLoading.toString())
            .set("Search", treeViewParametersGroupDTO.search)
            .set("skip", treeViewParametersGroupDTO.skip)
            .set("take", treeViewParametersGroupDTO.take);

        if (treeViewParametersGroupDTO.ids) {
            treeViewParametersGroupDTO.ids.forEach((id) => {
                params = params.append("Ids", id);
            });
        }

        if (treeViewParametersGroupDTO.parentId) {
            params = params.append("ParentId", treeViewParametersGroupDTO.parentId);
        }

        return this.http.get<TreeViewNodeDTO<GrupoTO>[]>(`${environment.user.url}/api/v1/Group/tree-view`, {
            params: params
        });
    }

    uploadFileCustomField(userId: string, document: File, fieldKey: string): Observable<any> {
        const formData = new FormData();
        formData.append("file", document);

        const params = new HttpParams().set("fieldKey", fieldKey);

        return this.http.post<any>(`${environment.user.url}/api/v1/users/${userId}/fileCustomField`, formData, {
            params: params
        });
    }

    deleteFileCustomField(userId: string, fieldKey: string): Observable<any> {
        const param = new HttpParams().set("fieldKey", fieldKey);

        return this.http.delete<any>(`${environment.user.url}/api/v1/users/${userId}/fileCustomField`, {
            params: param
        });
    }

    getUrlFileCustomField(userId: string, fieldKey: string): Observable<string> {
        const param = new HttpParams().set("fieldKey", fieldKey);

        return this.http.get(`${environment.user.url}/api/v1/users/${userId}/UrlFileCustomField`, {
            params: param,
            responseType: "text"
        });
    }

    saveCompleteRegistration(data: UserCompleteRegistrationDTO): Observable<UsuarioTO> {
        return this.http.patch<UsuarioTO>(
            `${environment.user.url}/api/v1/users/${data.id}/complete-registration`,
            data
        );
    }

    RequestEmailChange(userId: string, newEmail: string): Observable<any> {
        return this.http.post(
            `${environment.user.url}/api/v1/users/${userId}/requestEmailChange`,
            JSON.stringify(newEmail),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    changeEmail(userId: string, token: string): Observable<any> {
        return this.http.patch(`${environment.user.url}/api/v1/users/${userId}/changeEmail`, JSON.stringify(token), {
            headers: {
                "Content-Type": "application/json; charset=utf-8"
            }
        });
    }

    passwordChanged(userId: string): Observable<any> {
        return this.http.patch(`${environment.user.url}/api/v1/users/${userId}/password-changed`, null);
    }
}
